import { useContext } from "react";

import { InlineCheckboxListControl, InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { MultiSelectReviewedCareElement } from "@/Flowsheets/Models/CareElements/MultiSelectReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface ICareElementOptionsControlProps {
    isEditable: boolean;
    label: string;
    side: "row" | "column";
    element: MultiSelectReviewedCareElement;
    onChange: (element: MultiSelectReviewedCareElement) => void;
    options: InlineOption<string>[];
}

/**
 * CareElementOptionsControl component for rendering options control for text reviewed care elements.
 * @param props - The props for the component.
 * @property {MultiSelectReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @property {InlineOption<string>[]} props.options - The options to be displayed in the control.
 * @returns The rendered component.
 */
export function CareElementOptionsControl(props: Readonly<ICareElementOptionsControlProps>): JSX.Element {
    const { element, onChange, isEditable, label, side = "row" } = props;

    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
        >
            <InlineCheckboxListControl<string>
                id={`${element.Name}-options-control`}
                items={props.options}
                disabled={!isEditable}
                values={element.Value ?? []}
                onChange={options => {
                    const isValid = options.any();
                    const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;
                    let dateToUse = null;

                    if (isValid) {
                        dateToUse = shouldAutoFillDate
                            ? encounterDateTimeWithPrecision
                            : element.DateReviewedWithPrecision;
                    }

                    onChange({ ...element, Value: options, DateReviewedWithPrecision: dateToUse });
                }}
                isMultipleLines={side === "column"}
                additionalWrapperClassName="padding-lg-bottom"
            />
        </CareElementDateReviewedRow>
    );
}
